import {
  AnimatedSprite,
  Application,
  Assets,
  Container,
  Rectangle,
  Texture,
  Cache,
} from "pixi.js";
import { Position } from "types/battle-engine/battle";

export interface ProjectileConfig {
  app: Application;
  spriteUrl: string;
  spriteFrameCount: number;
  spriteScale?: number;
  from: Position;
  to: Position;
  durationMs: number;
  parent?: Container;
  onImpact?: () => void; // Callback when projectile reaches target
}

interface ProjectileMetadata {
  frameCount?: number;
  fps?: number;
}

const DEFAULT_FPS = 24;

/**
 * Projectile class handles visual projectile rendering
 * - Non-directional sprite that rotates toward target
 * - Linear interpolation from start to end position
 * - Auto-destroys on completion
 * - Supports animated sprite strips (loops while flying)
 */
export class Projectile {
  private sprite: AnimatedSprite | null = null;
  private container: Container;
  private startPos: Position;
  private endPos: Position;
  private duration: number;
  private startTime: number;
  private app: Application;
  private parent: Container;
  private isDestroyed: boolean = false;
  private tickerFn: ((time: any) => void) | null = null;
  private onImpact?: (() => void) | null;

  constructor(config: ProjectileConfig) {
    this.app = config.app;
    this.parent = config.parent ?? config.app.stage;
    this.startPos = config.from;
    this.endPos = config.to;
    this.duration = config.durationMs;
    this.startTime = 0; // Will be set when update loop starts
    this.onImpact = config.onImpact ?? null;

    // Create container
    this.container = new Container();
    this.container.x = config.from.x;
    this.container.y = config.from.y;
    this.container.zIndex = config.from.y;
    this.container.pivot.set(0.5);

    // Load sprite and start animation
    this.initSprite(
      config.spriteUrl,
      config.spriteFrameCount,
      config.spriteScale,
    );

    // Add to parent container
    this.parent.addChild(this.container);
  }

  /**
   * Load projectile sprite and set up initial rotation
   */
  private async initSprite(
    spriteUrl: string,
    spriteFrameCount: number,
    spriteScale?: number,
  ) {
    try {
      // Resolve the URL properly for Vite
      const texture = Cache.has(spriteUrl)
        ? Cache.get(spriteUrl)
        : await Assets.load<Texture>(spriteUrl);

      const frames = this.createFrames(
        texture,
        {
          frameCount: spriteFrameCount,
          fps: DEFAULT_FPS,
        },
        spriteUrl,
      );

      this.sprite = new AnimatedSprite(frames);
      this.sprite.anchor.set(0.5);
      this.sprite.scale.set(spriteScale ?? 1);
      this.sprite.loop = true; // Projectiles loop while flying
      this.sprite.animationSpeed = DEFAULT_FPS / 60; // Convert FPS to PixiJS speed (24fps / 60 ticker fps)

      // Calculate initial rotation
      this.updateRotation();
      this.container.addChild(this.sprite);

      this.sprite.play();

      // Start update loop
      this.startUpdate();
    } catch (error) {
      console.error("Failed to load projectile sprite:", spriteUrl, error);
      this.destroy();
    }
  }

  /**
   * Create animation frames from sprite strip
   * If frameCount not specified, treats as single frame
   */
  private createFrames(
    texture: Texture,
    metadata: ProjectileMetadata,
    spriteUrl: string,
  ): Texture[] {
    const frameCount = metadata.frameCount || 1;
    if (frameCount === 1) {
      return [texture];
    }

    const frames: Texture[] = [];
    const frameWidth = texture.width / frameCount;
    const frameHeight = texture.height;

    for (let i = 0; i < frameCount; i++) {
      const hasCachedTexture = Cache.has(spriteUrl + "__frame-" + i);
      if (hasCachedTexture) {
        frames.push(Cache.get(spriteUrl + "__frame-" + i));
        continue;
      }

      const frame = new Texture({
        label: spriteUrl + "__frame-" + i,
        source: texture.source,
        frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
      });
      frames.push(frame);

      Cache.set(spriteUrl + "__frame-" + i, frame);
    }

    return frames;
  }

  /**
   * Update rotation to point toward target
   * Constantly updates for smooth rotation as projectile moves
   */
  private updateRotation() {
    if (!this.sprite) return;

    const dx = this.endPos.x - this.container.x;
    const dy = this.endPos.y - this.container.y;

    // Calculate angle in radians
    this.sprite.rotation = Math.atan2(dy, dx);
  }

  /**
   * Start the update ticker for position interpolation
   */
  private startUpdate() {
    // Set start time NOW when update loop begins
    this.startTime = performance.now();

    this.tickerFn = (time: any) => {
      if (this.isDestroyed) return;

      const now = performance.now();
      const elapsed = now - this.startTime;
      const t = Math.min(1, elapsed / this.duration);

      // Linear interpolation
      this.container.x = Math.round(
        this.startPos.x + (this.endPos.x - this.startPos.x) * t,
      );
      this.container.y = Math.round(
        this.startPos.y + (this.endPos.y - this.startPos.y) * t,
      );

      // Update rotation to always point at target
      this.updateRotation();

      // Update zIndex
      this.container.zIndex = this.container.y;

      // Manually update the sprite animation (in case it's not auto-updating)
      if (this.sprite && this.sprite.playing) {
        this.sprite.update(time);
      }

      // Check completion
      if (t >= 1) {
        // Call onImpact before destroying
        if (this.onImpact) {
          this.onImpact();
        }
        this.destroy();
      }
    };

    this.app.ticker.add(this.tickerFn);
  }

  /**
   * Clean up resources and remove from stage
   */
  public destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    if (this.tickerFn) {
      this.app.ticker.remove(this.tickerFn);
      this.tickerFn = null;
    }

    this.container.destroy({ children: true });
  }
}
